import { useParams, Navigate, Link } from "react-router-dom";
import { useMemo } from "react";
import { blogPosts, blogCategories } from "../../../mocks/blog";
import { slugToCategory, categoryToSlug } from "../../../utils/blogSlug";
import BlogNavbar from "../components/BlogNavbar";
import BlogGrid from "../components/BlogGrid";
import NewsletterBanner from "../components/NewsletterBanner";
import Footer from "../../home/components/Footer";
import JsonLd from "../../../components/feature/JsonLd";
import { useSeoMeta } from "../../../hooks/useSeoMeta";

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://ltfsourcing.com";

const categoryDescriptions: Record<string, string> = {
  "Product Sourcing":
    "Factory vetting, supplier qualification, QC inspection, OEM & ODM strategy — everything you need to source smarter from global markets.",
  Wholesale:
    "Direct wholesale access, MOQ negotiation, margin optimisation, and import channel development for businesses buying at scale.",
  Inventory:
    "Overstock clearance, seasonal demand planning, liquidation strategy, and cash flow recovery — turning idle stock into working capital.",
  Logistics:
    "Incoterms explained, freight selection, customs brokerage, and landed cost optimisation for cross-border importers.",
  "Supply Chain":
    "Resilience strategy, dual-sourcing, nearshoring, RFQ process improvement, and supply chain intelligence for modern procurement teams.",
};

const categoryKeywords: Record<string, string> = {
  "Product Sourcing":
    "product sourcing, supplier verification, OEM ODM, factory audit, quality control, sourcing strategy",
  Wholesale:
    "wholesale buying, MOQ negotiation, bulk purchasing, import wholesale, wholesale suppliers",
  Inventory:
    "overstock inventory, liquidation, inventory clearance, surplus stock, inventory management",
  Logistics:
    "incoterms, freight forwarding, customs broker, shipping terms, landed cost, import logistics",
  "Supply Chain":
    "supply chain strategy, resilient supply chain, dual sourcing, nearshoring, RFQ process",
};

export default function BlogCategoryPage() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const category = categorySlug ? slugToCategory(categorySlug) : null;

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: blogPosts.length };
    blogCategories.slice(1).forEach((cat) => {
      counts[cat] = blogPosts.filter((p) => p.category === cat).length;
    });
    return counts;
  }, []);

  const seoTitle = category
    ? `${category} Articles & Guides | LTF Sourcing Blog`
    : "Blog Category | LTF Sourcing";
  const seoDescription = category
    ? `${categoryDescriptions[category] ?? ""} Browse all ${categoryCounts[category] ?? 0} ${category.toLowerCase()} articles on LTF Sourcing Blog.`
    : "";
  const seoCanonical = category
    ? `${SITE_URL}/blog/category/${categoryToSlug(category)}`
    : `${SITE_URL}/blog`;

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    canonical: seoCanonical,
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogType: "website",
    keywords: category ? categoryKeywords[category] ?? "" : "",
  });

  const collectionPageSchema = useMemo(() => {
    if (!category) return null;
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${category} Articles & Guides — LTF Sourcing Blog`,
      description: categoryDescriptions[category] ?? "",
      url: `${SITE_URL}/blog/category/${categoryToSlug(category)}`,
      publisher: {
        "@type": "Organization",
        name: "LTF Sourcing",
        url: SITE_URL,
      },
      inLanguage: "en",
      about: { "@type": "Thing", name: category },
    };
  }, [category]);

  const breadcrumbSchema = useMemo(() => {
    if (!category) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
        {
          "@type": "ListItem",
          position: 3,
          name: category,
          item: `${SITE_URL}/blog/category/${categoryToSlug(category)}`,
        },
      ],
    };
  }, [category]);

  if (!category) return <Navigate to="/blog" replace />;

  const count = categoryCounts[category] ?? 0;
  const description = categoryDescriptions[category] ?? "";

  return (
    <main className="min-h-screen w-full bg-white">
      {collectionPageSchema && (
        <JsonLd id={`schema-category-${categorySlug}`} schema={collectionPageSchema} />
      )}
      {breadcrumbSchema && (
        <JsonLd id={`schema-category-breadcrumb-${categorySlug}`} schema={breadcrumbSchema} />
      )}

      <BlogNavbar />

      <div className="pt-16">
        {/* Dark category hero */}
        <div className="bg-[#222831] py-14">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-white/40 mb-8">
              <a href="/" className="hover:text-[#C9A227] transition-colors cursor-pointer">
                Home
              </a>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/blog" className="hover:text-[#C9A227] transition-colors cursor-pointer">
                Blog
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white/70">{category}</span>
            </nav>

            <div className="flex items-end justify-between flex-wrap gap-6">
              <div className="flex-1 min-w-0">
                <p className="text-[#C9A227] text-xs font-bold uppercase tracking-widest mb-3">
                  Category
                </p>
                <h1 className="text-3xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
                  {category}
                </h1>
                <p className="text-white/50 text-sm lg:text-base leading-relaxed max-w-2xl">
                  {description}
                </p>
                <p className="text-white/30 text-xs mt-3">
                  {count} article{count !== 1 ? "s" : ""}
                </p>
              </div>

              <Link
                to="/blog"
                className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 text-white/60 hover:border-[#C9A227] hover:text-[#C9A227] text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-base"></i>
                All Articles
              </Link>
            </div>

            {/* Sibling category pills */}
            <div className="mt-8 flex flex-wrap gap-2">
              {blogCategories.slice(1).map((cat) => {
                const isActive = cat === category;
                return (
                  <Link
                    key={cat}
                    to={`/blog/category/${categoryToSlug(cat)}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      isActive
                        ? "bg-[#C9A227] text-white"
                        : "bg-white/10 text-white/60 hover:bg-white/20 hover:text-white"
                    }`}
                  >
                    {cat}
                    <span className="ml-1.5 opacity-70">{categoryCounts[cat] ?? 0}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Article grid — pre-filtered */}
        <div id="blog-grid">
          <BlogGrid activeCategory={category} search="" />
        </div>
      </div>

      <NewsletterBanner />
      <Footer />
    </main>
  );
}
